/**
 * Merge Meta's per-day series into the Google chart points (09-09): leads as
 * their own toggleable series, and cost / clicks / impressions ADDED into the
 * shared keys so the plotted lines match the blended tiles beneath the chart
 * (which already sum across platforms). CPA / ROAS keys are left untouched —
 * their tiles are Google-marked. Days past Meta's synced window carry Google
 * only; the chart footnote discloses that gap.
 *
 * Each Meta day lands in the last bucket whose rawDate is not after it, which
 * follows the day/week/month grouping without duplicating the hook's bucket
 * math. Days before the first bucket are dropped (outside the window).
 */
import type { MetaTimeSeriesRow } from '@/lib/types';
import { toDisplayAmount } from './money';

interface BlendablePoint {
  rawDate: string;
  spend: number;
  clicks: number;
  impressions: number;
  cost: number;
  cpc: number;
}

export function blendMetaIntoChartPoints<T extends BlendablePoint>(
  points: T[],
  metaRows: readonly MetaTimeSeriesRow[] | undefined,
): (T & { metaLeads: number })[] {
  const keys = points.map((p) => String(p.rawDate));
  const byBucket = new Map<string, { spend: number; clicks: number; impressions: number; leads: number }>();
  for (const row of metaRows ?? []) {
    const day = String(row.day);
    if (keys.length === 0 || day < keys[0]) continue;
    let bucket = keys[0];
    for (const k of keys) {
      if (k <= day) bucket = k;
      else break;
    }
    const acc = byBucket.get(bucket) ?? { spend: 0, clicks: 0, impressions: 0, leads: 0 };
    acc.spend += toDisplayAmount(row.spend_cents, 'meta');
    acc.clicks += row.clicks ?? 0;
    acc.impressions += row.impressions ?? 0;
    acc.leads += row.leads ?? 0;
    byBucket.set(bucket, acc);
  }
  return points.map((p) => {
    const m = byBucket.get(String(p.rawDate));
    if (!m) return { ...p, metaLeads: 0 };
    const spend = p.spend + m.spend;
    const clicks = p.clicks + m.clicks;
    return {
      ...p,
      metaLeads: m.leads,
      spend,
      cost: spend,
      clicks,
      impressions: p.impressions + m.impressions,
      // CPC blends legally (cost and clicks share units on both platforms),
      // keeping the line consistent with the blended CPC tile.
      cpc: clicks > 0 ? spend / clicks : 0,
    };
  });
}

/**
 * The last day Meta's sync covers, or null when there are no synced rows.
 * When it falls short of the window's end, the blended lines cannot cover the
 * tail — the chart footnote says so instead of letting the line dip silently.
 */
export function metaSyncedThrough(metaRows: readonly MetaTimeSeriesRow[] | undefined): string | null {
  let max = '';
  for (const row of metaRows ?? []) {
    const day = String(row.day);
    if (day > max) max = day;
  }
  return max || null;
}
